
import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { SharedQueryProvider } from './shared/providers/QueryProvider';
import { ErrorBoundary } from './shared/components/ErrorBoundary';
import { LanguageProvider } from './i18n/LanguageContext';
import { LanguageToggle } from './shared/components/LanguageToggle';
import './styles/globals.css';

import { ProtectedRoute } from './shared/components/ProtectedRoute';
import { HomeRedirect } from './shared/components/HomeRedirect';
import { LoginPage } from './shared/pages/Login';

// Lazy load the apps
const CustomerApp = lazy(() => import('./apps/customer/App'));
const WaiterApp = lazy(() => import('./apps/waiter/App'));
const AdminApp = lazy(() => import('./apps/admin/App'));
const ManagerApp = lazy(() => import('./apps/manager/App'));
const CashierApp = lazy(() => import('./apps/cashier/App'));
const ReceptionistApp = lazy(() => import('./apps/receptionist/ReceptionistApp').then(m => ({ default: m.ReceptionistApp })));

const Loading = () => (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-primary font-serif italic text-lg animate-pulse">قصر الهند | Qasr Al Hind</p>
    </div>
);

const RootApp = () => {
    return (
        <ErrorBoundary>
            <LanguageProvider>
                <SharedQueryProvider>
                    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                        <Suspense fallback={<Loading />}>
                            <Routes>
                                <Route path="/login" element={<LoginPage />} />

                                {/* Admin Routes (Super Admin Only) */}
                                <Route path="/admin/*" element={
                                    <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'DATA_AUDITOR']}>
                                        <AdminApp />
                                    </ProtectedRoute>
                                } />

                                <Route path="/manager/*" element={
                                    <ProtectedRoute allowedRoles={['BRANCH_MANAGER']}>
                                        <ManagerApp />
                                    </ProtectedRoute>
                                } />

                                {/* Waiter Routes */}
                                <Route path="/waiter/*" element={
                                    <ProtectedRoute allowedRoles={['WAITER']}>
                                        <WaiterApp />
                                    </ProtectedRoute>
                                } />

                                {/* Cashier Routes */}
                                <Route path="/cashier/*" element={
                                    <ProtectedRoute allowedRoles={['CASHIER']}>
                                        <CashierApp />
                                    </ProtectedRoute>
                                } />

                                <Route path="/receptionist/*" element={
                                    <ProtectedRoute allowedRoles={['RECEPTIONIST']}>
                                        <ReceptionistApp />
                                    </ProtectedRoute>
                                } />

                                {/* Customer Routes (default) with HomeRedirect */}
                                <Route path="/*" element={
                                    <HomeRedirect>
                                        <CustomerApp />
                                    </HomeRedirect>
                                } />
                            </Routes>
                        </Suspense>

                        {/* Global Language Toggle - appears on all pages */}
                        <LanguageToggle />
                    </BrowserRouter>
                </SharedQueryProvider>
            </LanguageProvider>
        </ErrorBoundary>
    );
};

// Global error handling for chunk load errors
window.addEventListener('error', (event) => {
    const error = event.error;
    if (error && (
        error.name === 'ChunkLoadError' ||
        (error.message && (
            error.message.includes('Failed to fetch dynamically imported module') ||
            error.message.includes('Loading chunk')
        ))
    )) {
        console.warn('Global ChunkLoadError detected');
        const hasReloaded = sessionStorage.getItem('chunk_error_reload');
        if (!hasReloaded) {
            sessionStorage.setItem('chunk_error_reload', 'true');
            window.location.reload();
        }
    }
}, true);

const container = document.getElementById('root');
if (container) {
    // Check if a root already exists on this element
    // @ts-ignore
    let root = container._qasrAlHindRoot;

    if (!root) {
        root = ReactDOM.createRoot(container);
        // @ts-ignore
        container._qasrAlHindRoot = root;
    }

    root.render(
        <React.StrictMode>
            <RootApp />
        </React.StrictMode>
    );
}
